import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { MapView, useMap, VertexEditSession, type MapViewport } from '@wehunt/map'
import type { AreaDisplayItem } from '../../lib/areaGrouping'
import { isPolygonSelectable } from '../../lib/mapAppearance'
import type { MapAppearance } from '../../lib/mapAppearance'
import type { GeoArea, GeoPt, TeamMap } from '../../lib/types'
import type { PropertyFeature } from './propertyTapResult'
import { EditorGroundLayers } from './editorGroundLayers'
import { EditorParcelLayer } from './editorParcelLayer'
import { EditorPinsAndLines } from './editorPinsAndLines'
import { EditorDraftLayer } from './editorDraftLayer'
import { EditorClickRouting } from './editorClickRouting'
import { resolveBaseSource, resolveOverlaySources } from './editorTileSources'
import { resolveVertexEditTarget } from './editorVertexTarget'
import type { AreaEditDraft, EditState, LayerId, LineDraft, PlaceMode, VertexEdit } from './mapLibreEditorTypes'
import type { PrintPinPreviewSpec } from '../../lib/printPinPreview'

export type { EditState, LayerId, PlaceMode, VertexEdit, LineDraft, AreaEditDraft }

// `MapView` reads `minZoom`/`maxZoom` once, at mount — see `ReportMap`'s own
// doc comment on the same pair for the full reasoning. 1/19 matches the old
// canvas's own effective bounds; the tile catalogue's real fetch bounds live
// on the raster SOURCE via `tangerRasterSource`'s `minZ`/`maxZ`.
const EDITOR_MIN_ZOOM = 1
const EDITOR_MAX_ZOOM = 19

const DEFAULT_MAP_CENTER = { lat: 63.27, lon: 17.88 }
const DEFAULT_TILE_ZOOM = 12
const DEFAULT_TILE_ZOOM_MAX = 19

export interface MapLibreEditorCanvasProps {
  printMap?: TeamMap
  state: EditState
  /** CLUB-676: pins and lines the viewer has hidden from the collection panel;
   *  not drawn. Per-viewer display state, never part of the edit — see
   *  `MapEditorPage`'s own `hiddenOnCanvas`. */
  hiddenObjectIds?: ReadonlySet<string>
  /** Pre-grouped borders/sub-areas/forbidden (memoized in the parent) so a
   *  property selection draws as ONE outline and pan/zoom re-renders don't
   *  re-run the polygon-clipping union. State stays per-parcel. */
  borderDisplay: AreaDisplayItem<GeoArea>[]
  subAreaDisplay: AreaDisplayItem<GeoArea>[]
  forbiddenDisplay: AreaDisplayItem<GeoArea>[]
  activeTab: LayerId
  drawing: boolean
  drawPoints: GeoPt[]
  drawAccent: string
  /** How the in-progress draw path is rendered — closed polygon
   *  (borders/areas) or an open line / arrow. */
  drawShape?: 'polygon' | 'line' | 'arrow'
  /** Wash the in-progress polygon — see `EditorDraftLayerProps.polygonFill`. */
  drawFill?: boolean
  onAddDrawPoint: (lat: number, lon: number) => void
  selectedId: string | null
  onSelectObj: (id: string | null) => void
  onMoveObj: (id: string, lat: number, lon: number) => void
  onAddObj: (lat: number, lon: number) => void
  placeMode: PlaceMode
  borderDraft: AreaEditDraft
  areaDraft: AreaEditDraft
  /**
   * When set, the named border/sub-area/forbidden-area/line is lifted out of
   * the static layers and into a `VertexEditSession` (terra-draw select
   * mode) instead — drag/insert/delete on its vertices flow back through
   * `onVertexMove`/`onVertexInsert`/`onVertexDelete` below. Resolution mirrors
   * `FullscreenMapCanvas`'s own `vertexTarget` lookup
   * (`editorVertexTarget.ts`'s `resolveVertexEditTarget`); a resolved
   * polygon target with more than one ring THROWS rather than corrupting —
   * `geometryEditable()` is supposed to keep such a shape from ever reaching
   * this prop.
   */
  vertexEdit: VertexEdit
  onVertexMove: (i: number, lat: number, lon: number) => void
  onVertexDragStart?: () => void
  onVertexInsert: (after: number, lat: number, lon: number) => void
  onVertexDelete: (i: number) => void
  onPolygonSelect: (kind: 'border' | 'subarea' | 'forbidden', id: string) => void
  appearance: MapAppearance
  showParcels: boolean
  mapProperties: PropertyFeature[]
  propertiesLoading: boolean
  selectedParcels: Set<string>
  onToggleParcel: (id: string) => void
  onIdentifyProperty?: (lat: number, lon: number) => void
  /** Seeds the camera on mount only — this canvas owns its camera
   *  (`MapView`) from then on; observe it via `onViewChange` rather than
   *  driving it. Replaces the old canvas's `mapCenter` + `onPanMap` pair. */
  mapCenter?: { lat: number; lon: number }
  tileZoom?: number
  /** Max zoom of the tile source, used to scale pin labels with zoom. */
  tileZoomMax?: number
  tileInfo?: { url: string; generation: number } | null
  overlayLayers?: { url: string; generation: number }[]
  /** The in-progress line/arrow draft — styles the draw path like the app. */
  lineDraft?: LineDraft | null
  /** Click on a committed line/arrow → open it for point editing. */
  onLineSelect?: (id: string) => void
  /** On-map ✓ / 🗑 while drawing a line (the app's floating buttons). */
  onFinishLineDraw?: () => void
  onCancelLineDraw?: () => void
  /** Reports the live camera after every pan/zoom and once on load — this
   *  canvas's replacement for the old engine's `onPanMap`: it owns its camera
   *  internally, so the page OBSERVES it rather than driving it. */
  onViewChange?: (view: MapViewport) => void
  /** Bridges the live maplibre-gl map instance out to the page once
   *  `MapView`'s style has loaded, so the page's own zoom buttons / keyboard
   *  handlers can drive `map.zoomIn()` etc. at the Milestone H swap. Called
   *  with `null` on unmount. */
  onMap?: (map: ReturnType<typeof useMap>) => void
  /**
   * Centre the map on a point, on demand — the editor's print mode uses this
   * to fit the camera to the true print frame (see `MapEditorPage`). Shaped
   * exactly like `TeamMapView`'s own `focus` prop, including the `nonce`: the
   * request is an EVENT, not a state, and two consecutive events can carry
   * identical coordinates. `usePrintFrame` re-fits whenever print mode is
   * reopened, and the fitted zoom it computes does not depend on where the
   * camera is when it runs — so a user who opens print mode, zooms in, closes
   * it and reopens it gets a request with the same lat, lon and zoom as the
   * last one. Matched on values, that request would be swallowed as
   * "unchanged" and the map would stay at the zoom the user left it at,
   * showing a frame that no longer fits. The nonce is what makes it an event.
   *
   * `zoom` is optional; omitted keeps the current zoom.
   */
  focus?: { lat: number; lon: number; zoom?: number; nonce: number; padding?: { top: number; right: number; bottom: number; left: number } } | null
  /**
   * Extra map content — the print frame's layers, a projection probe — mounted
   * inside `MapView`'s children so `useMap()` resolves for it. Rendered after
   * every other layer so it paints on top, mirroring `TeamMapView`'s own
   * `children` slot.
   */
  children?: ReactNode
  /**
   * The print sheet's corner ring while print mode is on: pins outside it are
   * drawn faint, since the frame's own dim mask is a map layer and cannot
   * reach a DOM marker (see `printDim.ts`). Passed straight through to
   * `EditorPinsAndLines`.
   */
  dimOutside?: GeoPt[] | null
  /**
   * While print mode is on: the panel's pin and label millimetres and the
   * sheet's scale, so pins are drawn at the size the paper will carry them.
   * Passed straight through to `EditorPinsAndLines`.
   */
  printPinSizes?: PrintPinPreviewSpec
}

/**
 * Applies a `focus` request to the live map — this canvas's counterpart to
 * `TeamMapView`'s own `MapFocus`. Rendered inside `MapView`'s children, the
 * only place `useMap()` resolves to the map instance.
 *
 * Keyed on the nonce ALONE: re-running whenever lat/lon are equal-but-new, or
 * on any parent re-render, would yank the map back to the last fit every time
 * the user panned away from it. `appliedNonce` is a ref owned by
 * `MapLibreEditorCanvas` itself — unlike `TeamMapView`, this canvas's own
 * `MapView` is never remounted by an identity change, so there is no
 * remount boundary the ref needs to outlive; it only has to survive this
 * component's own re-renders, which a plain `useRef` already does.
 */
function MapFocus({
  focus,
  appliedNonce,
}: {
  focus?: { lat: number; lon: number; zoom?: number; nonce: number; padding?: { top: number; right: number; bottom: number; left: number } } | null
  appliedNonce: { current: number | null }
}) {
  const map = useMap()
  const focusNonce = focus?.nonce
  // `focus` is read as of the nonce that asked for it, through a ref rather
  // than the dependency list — as a dependency it would re-centre on every
  // parent render.
  const latest = useRef(focus)
  latest.current = focus
  useEffect(() => {
    const request = latest.current
    if (focusNonce === undefined || !request || !map) return
    if (appliedNonce.current === focusNonce) return
    appliedNonce.current = focusNonce
    map.easeTo({
      center: [request.lon, request.lat],
      ...(request.padding ? { padding: request.padding } : {}),
      zoom: Math.max(EDITOR_MIN_ZOOM, Math.min(EDITOR_MAX_ZOOM, request.zoom ?? map.getZoom())),
    })
  }, [focusNonce, map, appliedNonce])
  return null
}

/** Bridges the live map instance to `onMap`, rendered inside `MapView`'s
 *  children so `useMap()` resolves to it. */
function MapBridge({ onMap }: { onMap?: (map: ReturnType<typeof useMap>) => void }) {
  const map = useMap()
  useEffect(() => {
    onMap?.(map)
    return () => onMap?.(null)
  }, [map, onMap])
  return null
}

/**
 * The MapLibre replacement for `MapEditorPage`'s internal `FullscreenMapCanvas`
 * — static parity (Milestone G Task 3) plus vertex editing (Task 4): a
 * non-null `vertexEdit` lifts the named border/sub-area/forbidden-area/line
 * out of the static layers and into a `VertexEditSession`.
 *
 * `MapEditorPage` renders this in place of `FullscreenMapCanvas` as of the
 * Milestone H swap, but it stays unexported from the ui barrel
 * (`libs/web/ui/src/index.ts`): it is still exactly what its predecessor
 * was — a `MapEditorPage` implementation detail with one consumer — not a
 * shared component another page is meant to reach for.
 */
export function MapLibreEditorCanvas({
  state,
  hiddenObjectIds,
  borderDisplay,
  subAreaDisplay,
  forbiddenDisplay,
  activeTab,
  drawing,
  drawPoints,
  drawAccent,
  drawShape = 'polygon',
  drawFill,
  onAddDrawPoint,
  selectedId,
  onSelectObj,
  onMoveObj,
  onAddObj,
  placeMode,
  borderDraft,
  areaDraft,
  onPolygonSelect,
  appearance,
  showParcels,
  mapProperties,
  propertiesLoading,
  selectedParcels,
  onToggleParcel,
  onIdentifyProperty,
  mapCenter = DEFAULT_MAP_CENTER,
  tileZoom = DEFAULT_TILE_ZOOM,
  tileZoomMax = DEFAULT_TILE_ZOOM_MAX,
  tileInfo = null,
  overlayLayers = [],
  vertexEdit,
  onVertexMove,
  onVertexDragStart,
  onVertexInsert,
  onVertexDelete,
  lineDraft = null,
  onLineSelect,
  onFinishLineDraw,
  onCancelLineDraw,
  onViewChange,
  onMap,
  focus,
  children,
  dimOutside,
  printPinSizes,
  printMap,
}: MapLibreEditorCanvasProps) {
  const [liveZoom, setLiveZoom] = useState(tileZoom)
  // Which focus request has already been applied — see `MapFocus` above.
  const appliedFocusNonce = useRef<number | null>(null)

  const handleViewChange = useCallback(
    (view: MapViewport) => {
      setLiveZoom(view.zoom)
      onViewChange?.(view)
    },
    [onViewChange],
  )

  // Memoized on the inputs that actually decide the resolved source's
  // content: `MapView`'s own reconciliation effect now keys off a content
  // signature rather than object identity (see its doc comment), so this is
  // hygiene against needless work, not a correctness requirement — but a
  // fresh object every render is still a fresh object every render, and nothing
  // here needs to be one.
  const baseSource = useMemo(() => resolveBaseSource(tileInfo), [tileInfo])
  const overlaySources = useMemo(() => resolveOverlaySources(overlayLayers), [overlayLayers])

  const blockInteraction = !!(drawing || placeMode || vertexEdit)
  const groundClickable = isPolygonSelectable({ drawing, vertexEditing: !!vertexEdit, placeMode: !!placeMode })
  const linesClickable = !blockInteraction && !!onLineSelect

  // Resolved eagerly, unconditionally: a multi-ring target must throw on
  // every render it's present for, not only while `VertexEditSession` is
  // literally being mounted — see `resolveVertexEditTarget`'s own doc
  // comment on why this is a THROW, not a silent skip.
  const vertexTarget = resolveVertexEditTarget(state, vertexEdit, state.borderColor)

  // Suppression ids for the static layers below — id-matched, not gated on
  // `vertexTarget` having actually resolved: `FullscreenMapCanvas` hid a
  // border/sub-area/forbidden/line by `vertexEdit.kind`+`.id` match alone
  // (`MapEditorPage.tsx:3891`, `:4019`), independent of whether its own
  // separately-resolved `vertexTarget` found a record for that id. Mirroring
  // that here means a stale `vertexEdit` (pointing at an id no longer in
  // `state`) still hides nothing extra, but never re-shows a shape whose
  // static render was already suppressed for kind/id-match reasons alone.
  const borderVertexId = vertexEdit?.kind === 'border' ? vertexEdit.id : undefined
  const subareaVertexId = vertexEdit?.kind === 'subarea' ? vertexEdit.id : undefined
  const forbiddenVertexId = vertexEdit?.kind === 'forbidden' ? vertexEdit.id : undefined
  const lineVertexId = vertexEdit?.kind === 'line' ? vertexEdit.id : undefined

  return (
    <MapView
      baseSource={baseSource}
      overlaySources={overlaySources}
      initialView={{ center: mapCenter, zoom: tileZoom }}
      minZoom={EDITOR_MIN_ZOOM}
      maxZoom={EDITOR_MAX_ZOOM}
      onViewChange={handleViewChange}
    >
      <MapBridge onMap={onMap} />

      <EditorGroundLayers
        printMap={printMap}
        dimOutside={dimOutside}
        borderDisplay={borderDisplay}
        subAreaDisplay={subAreaDisplay}
        forbiddenDisplay={forbiddenDisplay}
        activeTab={activeTab}
        appearance={appearance}
        teamColor={state.borderColor}
        selectedId={selectedId}
        clickable={groundClickable}
        showParcels={showParcels}
        borderEditingId={borderDraft?.editingId}
        borderVertexId={borderVertexId}
        areaEditingId={areaDraft?.editingId}
        subareaVertexId={subareaVertexId}
        forbiddenVertexId={forbiddenVertexId}
        zoom={liveZoom}
      />

      <EditorParcelLayer
        showParcels={showParcels}
        mapProperties={mapProperties}
        propertiesLoading={propertiesLoading}
        selectedParcels={selectedParcels}
        onToggleParcel={onToggleParcel}
      />

      <EditorPinsAndLines
        objects={state.objects}
        lines={state.lines}
        hiddenObjectIds={hiddenObjectIds}
        selectedId={selectedId}
        activeTab={activeTab}
        appearance={appearance}
        onSelectObj={onSelectObj}
        onMoveObj={onMoveObj}
        onLineSelect={onLineSelect}
        blockInteraction={blockInteraction}
        vertexEditLineId={lineVertexId}
        zoom={liveZoom}
        tileZoomMax={tileZoomMax}
        dimOutside={dimOutside}
        printPinSizes={printPinSizes}
      />

      {vertexTarget && (
        <VertexEditSession
          shape={{ kind: vertexTarget.kind, points: vertexTarget.points }}
          style={{ stroke: vertexTarget.stroke, fill: vertexTarget.fill }}
          onVertexMove={onVertexMove}
          onVertexInsert={onVertexInsert}
          onVertexDelete={onVertexDelete}
          onDragStart={onVertexDragStart}
        />
      )}

      <EditorDraftLayer
        drawing={drawing}
        drawPoints={drawPoints}
        drawAccent={drawAccent}
        drawShape={drawShape}
        polygonFill={drawFill}
        lineDraft={lineDraft}
        onFinishLineDraw={onFinishLineDraw}
        onCancelLineDraw={onCancelLineDraw}
      />

      <EditorClickRouting
        drawing={drawing}
        placeMode={placeMode}
        onIdentifyProperty={onIdentifyProperty}
        onAddDrawPoint={onAddDrawPoint}
        onAddObj={onAddObj}
        onToggleParcel={onToggleParcel}
        showParcels={showParcels}
        activeTab={activeTab}
        onSelectObj={onSelectObj}
        onPolygonSelect={onPolygonSelect}
        onLineSelect={onLineSelect}
        groundHitLayerMounted={groundClickable}
        linesHitLayerMounted={linesClickable}
      />

      <MapFocus focus={focus} appliedNonce={appliedFocusNonce} />
      {children}
    </MapView>
  )
}
